import React, { useState } from 'react';

import { DateTime } from 'luxon';
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  PopoverBody,
  PopoverHeader,
  Row,
} from 'react-bootstrap';
import { BsCaretDownFill } from 'react-icons/bs';
import { createConnector } from 'react-instantsearch-dom';

/**
 * Get the start date for the filter
 */
export function getStartDate(month?: number, year?: number) {
  if (month && year) {
    return DateTime.fromObject({
      year,
      month,
    })
      .startOf('month')
      .toSeconds();
  }

  // Default of having had a group in the last 3 years
  return DateTime.utc().startOf('hour').minus({ years: 3 }).toSeconds();
}

/**
 * Get the end date for the filter
 */
export function getEndDate(month?: number, year?: number) {
  if (month && year) {
    return DateTime.fromObject({
      year,
      month,
    })
      .endOf('month')
      .toSeconds();
  }

  // Default to any time in the future
  return DateTime.now().plus({ years: 50 }).toUTC().endOf('year').toSeconds();
}

/**
 * Create A Filter to Filter By Month in both `startTimestamp` and `endTimestamp`
 */
const connectFilterByMonth = createConnector({
  displayName: 'FilterByMonth',
  getProvidedProps(props, searchState) {
    const { month } = searchState;
    const { year } = searchState;

    if (!month || !year) {
      return {};
    }

    const currentRefinement = DateTime.fromObject({ month, year }).toFormat(
      'MM/yyyy',
    );

    // Connect the underlying component with the `currentRefinement`
    return { currentRefinement };
  },
  refine(props, searchState, nextRefinement) {
    if (!nextRefinement) {
      // reset in this case
      const { month, year, ...resetSearchState } = searchState;
      return {
        ...resetSearchState,
      };
    }

    // take the date input and split it into month and year
    const date = DateTime.fromISO(nextRefinement);
    // When the underlying component calls its `refine` prop,
    // we update the searchState with the provided refinement.
    return {
      // `searchState` represents the search state of *all* widgets. We need to extend it
      // instead of replacing it, otherwise other widgets will lose their respective state.
      ...searchState,
      month: date.month,
      year: date.year,
    };
  },
  getSearchParameters(searchParameters, props, searchState) {
    const month = searchState?.month;
    const year = searchState?.year;

    const startDate = getStartDate(month, year);

    const endDate = getEndDate(month, year);

    // When the `attributeForMyQuery` state entry changes, we update the query
    return searchParameters
      .setFacets(['startTimestamp', 'endTimestamp'])
      .addNumericRefinement('startTimestamp', '>=', startDate)
      .addNumericRefinement('endTimestamp', '<=', endDate);
  },
  cleanUp(props, searchState) {
    // When the widget is unmounted, we omit the entry `attributeForMyQuery`
    // from the `searchState`, then on the next request the query will
    // be empty
    const { month, year, ...nextSearchState } = searchState;

    return nextSearchState;
  },
});

interface Props {
  /** a function to change the current query */
  refine: (...args: any[]) => any;
  currentRefinement?: any;
}

export function FilterByMonthInner({ currentRefinement, refine }: Props) {
  const [show, setShow] = useState(false);

  const currentYear = DateTime.now().year;
  // const currentMonth = DateTime.now().month;
  const [year, setYear] = useState(currentYear);

  const years = Array.from(Array(5).keys()).map((y) => currentYear + y);

  const defaultValue = currentRefinement
    ? DateTime.fromFormat(currentRefinement, 'MM/yyyy').toFormat('LLLL yyyy')
    : 'Filter by Month';

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const handleReset = () => {
    refine('');
    setShow(!show);
  };

  const search = (month: string) => {
    const searchDate = DateTime.fromFormat(`${month}/${year}`, 'LLLL/yyyy');
    refine(searchDate.toISO());
    setShow(!show);
  };

  const popover = (
    <Popover id="popover-contained">
      <PopoverHeader as="h3">
        <Row>
          <Col>
            <Form.Select onChange={handleYearChange} value={year}>
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </PopoverHeader>
      <PopoverBody>
        <Row>
          {months.map((month) => (
            <Col className="p-2" key={month} xs={6} sm={6}>
              <div className="d-grid gap-2">
                <Button onClick={() => search(month)} variant="light">
                  {month}
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      </PopoverBody>
      <PopoverBody>
        <div className="d-grid gap-2">
          <Button onClick={handleReset} variant="light">
            Show all months
          </Button>
          <Button onClick={() => setShow(!show)} variant="light">
            Close
          </Button>
        </div>
      </PopoverBody>
    </Popover>
  );

  return (
    <div>
      <OverlayTrigger
        show={show}
        trigger="click"
        placement="bottom"
        onToggle={() => setShow(!show)}
        overlay={popover}
      >
        <div className="d-grid gap-2">
          <Form.Label>Dates</Form.Label>
          <Button
            variant="light"
            style={{
              background: 'none',
              border: '1px solid #ced4da',
            }}
          >
            <>
              {defaultValue || 'Filter by Month'} <BsCaretDownFill />
            </>
          </Button>
        </div>
      </OverlayTrigger>
    </div>
  );
}

const FilterByMonth = connectFilterByMonth(FilterByMonthInner);

export default FilterByMonth;
